import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Divider,
  LinearProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import ReactMarkdown from 'react-markdown';
import { apiService } from '../api';

const MemberPerformance = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();

  const [member, setMember] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [viewReviewDialog, setViewReviewDialog] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    loadData();
  }, [memberId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load team member info
      const memberResponse = await apiService.getTeamMember(memberId);
      setMember(memberResponse.data.member);

      // Load documents
      const docsResponse = await apiService.getPerformanceDocuments(memberId);
      setDocuments(docsResponse.data.documents || []);

      // Load reviews
      const reviewsResponse = await apiService.getPerformanceReviews(memberId);
      setReviews(reviewsResponse.data.reviews || []);

      setError('');
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load team member data');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (documents.length >= 50) {
      setError('Maximum 50 documents allowed per team member');
      return;
    }

    const remainingSlots = 50 - documents.length;
    const filesToUpload = acceptedFiles.slice(0, remainingSlots);

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        await apiService.uploadPerformanceDocument(memberId, file);
        setUploadProgress(((i + 1) / filesToUpload.length) * 100);
      }

      await loadData();
      setError('');
    } catch (err) {
      console.error('Error uploading documents:', err);
      setError(err.response?.data?.error || 'Failed to upload documents');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    maxSize: 10485760, // 10MB
    disabled: uploading || documents.length >= 50
  });

  const handleDocumentToggle = (documentId) => {
    setSelectedDocuments(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        if (prev.length >= 50) {
          setError('Maximum 50 documents can be selected for review');
          return prev;
        }
        return [...prev, documentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(documents.slice(0, 50).map(doc => doc.id));
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await apiService.deletePerformanceDocument(documentId);
      await loadData();
      setSelectedDocuments(prev => prev.filter(id => id !== documentId));
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    }
  };

  const handleGenerateReview = async () => {
    if (selectedDocuments.length === 0) {
      setError('Please select at least one document for the performance review');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await apiService.generatePerformanceReview(memberId, selectedDocuments);
      await loadData();
      setSelectedDocuments([]);
      
      // Auto-open the generated review
      setSelectedReview(response.data);
      setViewReviewDialog(true);
    } catch (err) {
      console.error('Error generating review:', err);
      setError(err.response?.data?.error || 'Failed to generate performance review');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewReview = (review) => {
    setSelectedReview(review);
    setViewReviewDialog(true);
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      await apiService.deletePerformanceReview(reviewId);
      await loadData();
      if (selectedReview?.id === reviewId) {
        setViewReviewDialog(false);
        setSelectedReview(null);
      }
    } catch (err) {
      console.error('Error deleting review:', err);
      setError('Failed to delete review');
    }
  };

  const getFileIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf':
        return <PdfIcon color="error" />;
      case 'docx':
      case 'doc':
        return <DocIcon color="primary" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'image':
        return <ImageIcon color="success" />;
      default:
        return <FileIcon />;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    if (mb < 1) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!member) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Team member not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/team')} sx={{ mt: 2 }}>
          Back to Team
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/team')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ width: 60, height: 60, mr: 2, bgcolor: 'primary.main' }}>
            {member.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </Avatar>
          <Box>
            <Typography variant="h4">{member.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip label={member.role} color="primary" size="small" />
              <Chip label={member.specialty} variant="outlined" size="small" />
            </Box>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Documents Uploaded
              </Typography>
              <Typography variant="h3">
                {documents.length}/50
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(documents.length / 50) * 100} 
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Documents Selected
              </Typography>
              <Typography variant="h3">
                {selectedDocuments.length}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                for performance review
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Reviews Generated
              </Typography>
              <Typography variant="h3">
                {reviews.length}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                total performance reviews
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Document Upload */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Upload Documents for Performance Review
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Upload up to 50 documents (PDF, Word, Images, Text files). These will be analyzed by AI to generate comprehensive performance reviews.
        </Typography>

        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            bgcolor: isDragActive ? 'action.hover' : 'background.paper',
            cursor: uploading || documents.length >= 50 ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s',
            opacity: uploading || documents.length >= 50 ? 0.5 : 1
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          {uploading ? (
            <Box>
              <Typography variant="h6">Uploading...</Typography>
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 2, maxWidth: 400, mx: 'auto' }} />
              <Typography variant="body2" sx={{ mt: 1 }}>{Math.round(uploadProgress)}%</Typography>
            </Box>
          ) : documents.length >= 50 ? (
            <Typography variant="h6" color="error">
              Maximum document limit reached (50/50)
            </Typography>
          ) : (
            <Box>
              <Typography variant="h6">
                {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to select'}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Supported: PDF, Word, Images, Text (Max 10MB per file)
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {50 - documents.length} slots remaining
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Documents List */}
      {documents.length > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Uploaded Documents ({documents.length})
            </Typography>
            <Box>
              <Button
                size="small"
                onClick={handleSelectAll}
                sx={{ mr: 1 }}
              >
                {selectedDocuments.length === documents.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={20} /> : <AssessmentIcon />}
                onClick={handleGenerateReview}
                disabled={generating || selectedDocuments.length === 0}
              >
                {generating ? 'Generating Review...' : `Generate Review (${selectedDocuments.length})`}
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDocuments.length === documents.length && documents.length > 0}
                      indeterminate={selectedDocuments.length > 0 && selectedDocuments.length < documents.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedDocuments.includes(doc.id)}
                        onChange={() => handleDocumentToggle(doc.id)}
                      />
                    </TableCell>
                    <TableCell>{getFileIcon(doc.type)}</TableCell>
                    <TableCell>{doc.name}</TableCell>
                    <TableCell>{formatFileSize(doc.size)}</TableCell>
                    <TableCell>{formatDate(doc.uploaded_at)}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDocument(doc.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Previous Reviews */}
      {reviews.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Performance Reviews ({reviews.length})
          </Typography>
          <List>
            {reviews.map((review, index) => (
              <React.Fragment key={review.id}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">
                          Review #{reviews.length - index}
                        </Typography>
                        {review.enhanced_with_legal_context && (
                          <Chip label="Enhanced with Legal Context" size="small" color="primary" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Generated: {formatDate(review.generated_at)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Documents Analyzed: {review.documents_analyzed} | 
                          Total Words: {review.metrics?.total_words?.toLocaleString()} | 
                          Complexity Score: {review.metrics?.complexity_score}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleViewReview(review)}
                      color="primary"
                      sx={{ mr: 1 }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteReview(review.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < reviews.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* View Review Dialog */}
      <Dialog
        open={viewReviewDialog}
        onClose={() => setViewReviewDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Performance Review
          {selectedReview && selectedReview.enhanced_with_legal_context && (
            <Chip label="Enhanced with Legal Context" size="small" color="primary" sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedReview && (
            <Box>
              {/* Metrics */}
              {selectedReview.metrics && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Review Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Documents Analyzed
                      </Typography>
                      <Typography variant="h6">
                        {selectedReview.documents_analyzed}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Total Words
                      </Typography>
                      <Typography variant="h6">
                        {selectedReview.metrics.total_words?.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Legal Density
                      </Typography>
                      <Typography variant="h6">
                        {selectedReview.metrics.legal_terminology_density}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Complexity Score
                      </Typography>
                      <Typography variant="h6">
                        {selectedReview.metrics.complexity_score}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Review Text */}
              <Box sx={{ 
                '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 3, mb: 2 },
                '& p': { mb: 2 },
                '& ul, & ol': { mb: 2, pl: 4 }
              }}>
                <ReactMarkdown>{selectedReview.review || selectedReview.review_text}</ReactMarkdown>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewReviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MemberPerformance;
